import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Contributor } from "@/components/MirrorScore";
import { computeStreak } from "@/lib/analytics/engine";

interface MirrorScoreData {
  score: number;
  contributors: Contributor[];
  mirrorMessage: string | null;
  loading: boolean;
}

export const useMirrorScore = (): MirrorScoreData => {
  const [data, setData] = useState<MirrorScoreData>({
    score: 0,
    contributors: [],
    mirrorMessage: null,
    loading: true,
  });

  useEffect(() => {
    const calculate = async () => {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const last48h = new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString();

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setData({ score: 0, contributors: [], mirrorMessage: null, loading: false });
        return;
      }

      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayISO = todayStart.toISOString();

      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

      const [checkinRes, tasksRes, streakRes, habitsRes, habitCompRes, goalProgressRes] = await Promise.all([
        supabase
          .from("daily_checkins")
          .select("mood, energy, checked_in_at")
          .eq("user_id", user.id)
          .gte("checked_in_at", last48h)
          .lte("checked_in_at", new Date().toISOString())
          .order("checked_in_at", { ascending: false })
          .limit(1),

        supabase
          .from("tasks")
          .select("done")
          .eq("user_id", user.id)
          .gte("created_at", todayISO),

        (supabase as any)
          .from("xp_events")
          .select("created_at")
          .eq("user_id", user.id)
          .gte("created_at", thirtyDaysAgo)
          .order("created_at", { ascending: false }),

        supabase
          .from("habits")
          .select("name, is_active")
          .eq("user_id", user.id)
          .eq("is_active", true)
          .limit(3),

        supabase
          .from("habit_completions")
          .select("id")
          .eq("user_id", user.id)
          .gte("completed_date", weekAgo),

        supabase
          .from("goals")
          .select("progress_override")
          .eq("user_id", user.id)
          .eq("is_completed", false),
      ]);

      const checkin = checkinRes.error ? null : (checkinRes.data?.[0] ?? null);
      const hasCheckin = !!checkin;
      const moodValue = checkin ? Math.round(((checkin.mood - 1) / 4) * 100) : 0;
      const energyValue = checkin ? Math.round(((checkin.energy - 1) / 4) * 100) : 0;

      // Discipline score: completions this week / (active habits * 7) * 100
      const activeHabitCount = (habitsRes.data || []).length;
      const completionsThisWeek = (habitCompRes.data || []).length;
      const expectedCompletions = activeHabitCount * 7;
      const disciplineValue: number | null = expectedCompletions > 0
        ? Math.round((completionsThisWeek / expectedCompletions) * 100)
        : null;

      // Confidence score: average progress % on active goals
      const goalsWithProgress = (goalProgressRes.data || []).filter((g: any) => g.progress_override != null);
      const confidenceValue: number | null = goalsWithProgress.length > 0
        ? Math.round(goalsWithProgress.reduce((sum: number, g: any) => sum + (g.progress_override || 0), 0) / goalsWithProgress.length)
        : null;

      // Focus = average of discipline & confidence; partial if only one is available
      const hasFocusData = disciplineValue !== null || confidenceValue !== null;
      let focusValue = 0;
      if (disciplineValue !== null && confidenceValue !== null) {
        focusValue = Math.round((disciplineValue + confidenceValue) / 2);
      } else if (disciplineValue !== null) {
        focusValue = disciplineValue;
      } else if (confidenceValue !== null) {
        focusValue = confidenceValue;
      }

      const tasks = tasksRes.error ? [] : (tasksRes.data ?? []);
      const completedTasks = tasks.filter((t) => t.done).length;
      const totalTasks = tasks.length;
      const tasksValue = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
      const hasTasks = totalTasks > 0;

      const eventRows = streakRes.error ? [] : (streakRes.data ?? []);
      // Streak — ONE canonical definition (analytics engine). created_at is a
      // timestamptz; the engine buckets by LOCAL day, fixing the old UTC
      // toISOString() day-string drift, with the today|yesterday grace.
      const currentStreak = computeStreak(
        eventRows.map((e: any) => new Date(e.created_at).getTime()),
      ).current;
      const streakValue = Math.min(Math.round((currentStreak / 7) * 100), 100);
      const hasStreak = currentStreak > 0;

      type Dimension = { value: number; weight: number; hasData: boolean };
      const dimensions: Dimension[] = [
        { value: moodValue, weight: 0.20, hasData: hasCheckin },
        { value: energyValue, weight: 0.20, hasData: hasCheckin },
        { value: focusValue, weight: 0.20, hasData: hasFocusData },
        { value: tasksValue, weight: 0.25, hasData: hasTasks },
        { value: streakValue, weight: 0.15, hasData: hasStreak },
      ];

      const activeDimensions = dimensions.filter((d) => d.hasData);
      let finalScore = 0;
      if (activeDimensions.length > 0) {
        const totalWeight = activeDimensions.reduce((s, d) => s + d.weight, 0);
        const weightedSum = activeDimensions.reduce((s, d) => s + d.value * d.weight, 0);
        finalScore = Math.round(weightedSum / totalWeight);
      }

      const contributors: Contributor[] = [
        { name: "mood", value: moodValue, icon: "Smile", route: "/check-in", hasData: hasCheckin, rawKey: "score.mood" },
        { name: "energy", value: energyValue, icon: "Zap", route: "/check-in", hasData: hasCheckin, rawKey: "score.energy" },
        { name: "focus", value: focusValue, icon: "Crosshair", route: "/check-in", hasData: hasFocusData, rawKey: "score.focus" },
        { name: "tasks", value: tasksValue, icon: "Target", route: "/tasks", hasData: hasTasks, rawKey: "score.tasks" },
        { name: "streak", value: streakValue, icon: "Flame", route: "/check-in", hasData: hasStreak, rawKey: "score.streak" },
      ];

      let mirrorMessage: string | null = null;
      if (activeDimensions.length === 0) {
        mirrorMessage = "score.signal.starting";
      } else if (currentStreak >= 7) {
        mirrorMessage = `score.signal.streakStrong:${currentStreak}`;
      } else if (currentStreak >= 3) {
        mirrorMessage = `score.signal.streakBuilding:${currentStreak}`;
      } else if (finalScore >= 70) {
        mirrorMessage = "score.signal.ontrack";
      } else if (finalScore < 40 && !hasCheckin) {
        mirrorMessage = "score.signal.checkinPending";
      } else {
        mirrorMessage = null;
      }

      setData({ score: finalScore, contributors, mirrorMessage, loading: false });
    };

    calculate();
  }, []);

  return data;
};
